/**
 * Canonical subscription-status vocabulary — single source of truth for the
 * tenant app. Mirrors:
 *   - backend  app/modules/billing/state_machine.py (VALID_TRANSITIONS + the
 *     ALL_STATUSES / DUNNING_STATUSES / MEMBER_ACTIVE_STATUSES / MRR_STATUSES
 *     constants)
 *   - console  console/src/constants/subscriptionStatus.ts (identical copy)
 *
 * There is no shared JS/TS package between the tenant app and the console
 * today, so this definition is duplicated verbatim in both. Keep the three
 * copies (backend + these two) in lock-step; the exhaustive `satisfies`
 * checks below make drift a type error at build time.
 */

export const SUBSCRIPTION_STATUSES = [
  'trialing',
  'active',
  'past_due',
  'grace_period',
  'account_hold',
  'incomplete',
  'incomplete_expired',
  'paused',
  'canceled',
] as const;

export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

/**
 * Dunning states — the last payment failed and the tenant is on the clock
 * before service suspends. Drives the DunningBanner.
 */
export const DUNNING_STATUSES = [
  'past_due',
  'grace_period',
  'account_hold',
] as const satisfies readonly SubscriptionStatus[];

/**
 * Still-entitled membership — healthy (active/trialing) plus every dunning
 * state (grace_period/account_hold/past_due are still-entitled-but-dunning).
 * NOT member-active: canceled, paused, incomplete, incomplete_expired.
 */
export const MEMBER_ACTIVE_STATUSES = [
  'active',
  'trialing',
  'past_due',
  'grace_period',
  'account_hold',
] as const satisfies readonly SubscriptionStatus[];

const DUNNING_SET = new Set<string>(DUNNING_STATUSES);
const MEMBER_ACTIVE_SET = new Set<string>(MEMBER_ACTIVE_STATUSES);

/** True while the subscription is in a payment-failed dunning state. */
export function isDunning(status: string | null | undefined): boolean {
  return status != null && DUNNING_SET.has(status);
}

/** True while the subscription still confers live membership/entitlement. */
export function isMemberActive(status: string | null | undefined): boolean {
  return status != null && MEMBER_ACTIVE_SET.has(status);
}
